/**
 * Former - one instance per <form>: values/fill/reset, dirty tracking with an
 * unsaved-changes guard, and the submit pipeline (validator:submit -> busy
 * button -> g.fetch -> toasts / data.fields mapping / close + refresh).
 *
 * The form itself is authored by the app (HTML, tabs, custom JS - anything);
 * former only reads it, writes it and ships it. Fields without a `name` are
 * UI-only and invisible to former; `former-ignore` opts a named field out.
 *
 * Options (g.former.defaults < g.former(form, options) < former-* attributes):
 *   endpoint   g.fetch endpoint; absent = no submit pipeline, the app keeps
 *              its own validator:submit handler
 *   refresh    lister element/selector to .reload() after a save, or a
 *              function (res) - absent = nothing
 *   guard      true = confirm before closing the parent modal/offcanvas (or
 *              leaving the page) while dirty
 *   close      false = do not auto-close the parent modal/offcanvas on save
 *   toast      success toast text ('' = default "Saved.", false = none)
 *   onFill(record, form)   after fill/reset set the values, before the dirty
 *              snapshot - show/hide conditional sections here
 *   beforeSend(data, form) tweak the payload; return an object to replace it,
 *              false to cancel the send
 *   onSuccess(res, former) custom success handling; return false to skip the
 *              defaults (toast + close + refresh); a returned Promise is
 *              AWAITED with the form still locked and the buttons busy
 *
 * Events on the form (all bubble): former:fill { record }, former:dirty
 * { dirty }, former:saved { res, data }, former:error { res, data }.
 */
import { fieldGroups, readValues, applyValue, serialize } from './values.js';

const t = (text) => window.g.translate(text);
const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);

// Tweakable via g.former.defaults.<key> = ...
export const defaults = {
	close: true,   // auto-close the parent modal/offcanvas after a successful save
	guard: false,  // unsaved-changes confirm on close/leave
	toast: '',     // success toast text; '' = the default message, false = none
};

// former-* attributes on the <form> (the strongest config layer).
function attrOptions(form) {
	const opts = {};
	const attr = (name) => (form.hasAttribute(name) ? form.getAttribute(name) : form.getAttribute('data-' + name));
	const has = (name) => form.hasAttribute(name) || form.hasAttribute('data-' + name);
	if (has('former-endpoint')) opts.endpoint = attr('former-endpoint');
	if (has('former-refresh')) opts.refresh = attr('former-refresh');
	if (has('former-guard')) opts.guard = attr('former-guard') !== 'false';
	if (has('former-close')) opts.close = attr('former-close') !== 'false';
	if (has('former-toast')) opts.toast = attr('former-toast') === 'false' ? false : attr('former-toast');
	return opts;
}

export class Former {
	constructor(form, options) {
		this.form = form;
		form.former = this;
		this.opts = { ...defaults, ...options, ...attrOptions(form) };
		this.dirty = false;
		this.filling = false;
		this.bypass = false;
		this.sending = false;
		this.serverErrors = new Map(); // name -> el, field errors applied from data.fields

		// The baseline (authored defaults + clean snapshot) is captured lazily,
		// on first use - by then the component libraries have initialized.
		this.initial = null;
		this.clean = null;

		form.addEventListener('change', (e) => this.onMutate(e));
		form.addEventListener('input', (e) => this.onMutate(e));

		if (this.opts.endpoint) {
			// On a wizard form, the wizard drives the submit (it calls former.send()
			// on Finish), so former does NOT auto-send on validator:submit / native
			// submit - those fire on every step. Checked at event time so init order
			// with the wizard does not matter.
			form.addEventListener('validator:submit', () => { if (!form.hasAttribute('wizard')) this.send(); });
			form.addEventListener('submit', (e) => {
				e.preventDefault(); // never a native submit (validator does this too)
				if (!form.validator && !form.hasAttribute('wizard')) this.send();
			});
		}
		if (this.opts.guard) this.bindGuard();
	}

	baseline() {
		if (!this.initial) {
			this.initial = readValues(this.form, true); // authored defaults, inactive sections included
			this.clean = serialize(this.values());
		}
	}

	// ---- values / fill / reset --------------------------------------------------------

	values() {
		return readValues(this.form);
	}

	// Set every named field from `record`, falling back to the authored
	// defaults for keys the record does not carry - so fill(user) after
	// fill(otherUser) never leaks values. fill(null) = just the defaults.
	// Disabled fields are written too (onFill may enable their section right
	// after). Async because selekt.setValue resolves labels through a lookup -
	// awaited here so the clean snapshot is taken on the settled form.
	// Runs onFill + former:fill (conditional sections), then snapshots clean.
	async fill(record) {
		this.baseline();
		const rec = record || {};
		this.editedWhileFilling = false;
		this.filling = true;
		try {
			const groups = fieldGroups(this.form, true);
			// date-selekt range end fields are written by their instance
			const rangeEnds = new Set();
			for (const [, group] of groups) {
				const ds = group[0].dateSelekt;
				if (ds && ds.range) rangeEnds.add(ds.endInput.name);
			}
			const pending = [];
			for (const [name, group] of groups) {
				if (rangeEnds.has(name)) continue;
				const value = hasOwn(rec, name) ? rec[name] : this.initial[name];
				const ds = group[0].dateSelekt;
				if (ds && ds.range) {
					const endName = ds.endInput.name;
					ds.setValue(value == null ? null : value, (hasOwn(rec, endName) ? rec[endName] : this.initial[endName]) || null);
					continue;
				}
				const out = applyValue(group, value);
				if (out && typeof out.then === 'function') pending.push(out);
			}
			if (pending.length) await Promise.all(pending);
			this.clearServerErrors();
			if (this.form.validator) this.form.validator.reset();
			if (this.opts.onFill) this.opts.onFill(record || null, this.form);
			this.emit('former:fill', { record: record || null });
		} finally {
			this.filling = false;
		}
		if (this.editedWhileFilling) {
			// the user TYPED during the awaited fill (the race-conditions
			// scan): absorbing that edit into the clean snapshot would blind
			// the unsaved-changes guard — keep the previous baseline and
			// stand dirty instead
			this.editedWhileFilling = false;
			this.setDirty(true);
			return this;
		}
		this.clean = serialize(this.values());
		this.setDirty(false);
		return this;
	}

	// Back to the authored defaults. The native reset restores the UI-only
	// (unnamed) fields too; fill(null) then settles every named field
	// (our components restore async on a native reset).
	reset() {
		this.baseline();
		this.filling = true;
		this.form.reset();
		this.filling = false;
		return this.fill(null);
	}

	// ---- dirty ------------------------------------------------------------------------

	isDirty() {
		this.baseline();
		return serialize(this.values()) !== this.clean;
	}

	markClean() {
		this.clean = serialize(this.values());
		this.setDirty(false);
		return this;
	}

	onMutate(e) {
		if (this.filling) {
			// only REAL user input counts (components fire synthetic events
			// while fill writes them — those are the fill itself)
			if (e.isTrusted) this.editedWhileFilling = true;
			return;
		}
		const name = e.target && e.target.name;
		if (name && this.serverErrors.has(name)) {
			window.g.fieldError(this.serverErrors.get(name), null);
			this.serverErrors.delete(name);
		}
		this.baseline();
		this.setDirty(serialize(this.values()) !== this.clean);
	}

	setDirty(dirty) {
		if (dirty === this.dirty) return;
		this.dirty = dirty;
		this.emit('former:dirty', { dirty });
	}

	// ---- unsaved-changes guard --------------------------------------------------------

	container() {
		return this.form.closest('.modal, .offcanvas');
	}

	bindGuard() {
		const box = this.container();
		if (box) {
			const onHide = (e) => {
				if (this.bypass || !this.isDirty()) return;
				e.preventDefault();
				// one question at a time: a double click on the × asked twice
				// before the first dialog's backdrop was up (the flag is the
				// guard — the call opts out of the g.action detector)
				if (this.asking) return;
				this.asking = true;
				window.g.confirm(t('You have unsaved changes. Discard them?'), { title: t('Unsaved changes'), danger: true, confirmText: t('Discard'), unguarded: true }).then((ok) => {
					this.asking = false;
					if (!ok) return;
					this.markClean();
					this.close();
				});
			};
			box.addEventListener('hide.bs.modal', onHide);
			box.addEventListener('hide.bs.offcanvas', onHide);
		} else {
			window.addEventListener('beforeunload', (e) => {
				if (!this.isDirty()) return;
				e.preventDefault();
				e.returnValue = ''; // Chrome requires it for the native prompt
			});
		}
	}

	// Close the parent modal/offcanvas (bypasses the guard).
	close() {
		const box = this.container();
		if (!box || !window.bootstrap) return;
		const inst = box.classList.contains('offcanvas') ? window.bootstrap.Offcanvas.getInstance(box) : window.bootstrap.Modal.getInstance(box);
		if (!inst) return;
		this.bypass = true;
		inst.hide();
		this.bypass = false;
	}

	// ---- submit pipeline --------------------------------------------------------------

	submit() {
		this.form.requestSubmit(); // routes through the validator when present
	}

	async send() {
		// ONE send at a time: the pipeline runs through g.action keyed by the
		// FORM (the lock is taken synchronously, before any await), so a
		// double-click (or a second submit during an async beforeSend) can
		// never POST twice — a create endpoint would mint two records. Every
		// submit control goes busy, not just the first match: a wizard form
		// keeps a hidden Next BEFORE the visible Finish, and busying only the
		// first submit left Finish enabled under the double-click. The lock
		// (and the busy state) holds until onSuccess and the refresh SETTLE —
		// an async onSuccess keeps the form locked for its whole road.
		if (window.g.action.locked(this.form)) return null;
		const buttons = [...this.form.querySelectorAll('button[type="submit"], input[type="submit"]')];
		return window.g.action(buttons, () => this.pipeline(), { key: this.form });
	}

	async pipeline() {
		this.sending = true;
		try {
			let data = this.values();
			if (this.opts.beforeSend) {
				const out = await this.opts.beforeSend(data, this.form);
				if (out === false) return null;
				if (out && typeof out === 'object') data = out;
			}
			// former:send - listeners may mutate/replace detail.data (change the
			// payload) or preventDefault() to cancel the send. Sync (for async work
			// use the opts.beforeSend callback).
			const sendEv = this.emit('former:send', { data }, true);
			if (sendEv.defaultPrevented) return null;
			data = sendEv.detail.data;

			let res = await window.g.fetch(this.opts.endpoint, data);

			// former:response - listeners may mutate/replace detail.res before former
			// inspects the envelope (e.g. normalize an external API's shape).
			res = this.emit('former:response', { res, data }).detail.res;

			if (!res.success) {
				window.g.error(res.error.message || t('Something went wrong. Please try again.'));
				this.emit('former:error', { res, data });
				return res;
			}
			if (res.error && res.error.code > 0) {
				if (res.error.message) window.g.warning(res.error.message);
				this.emit('former:error', { res, data });
				return res;
			}
			if (res.data && res.data.validated === false) {
				this.applyFieldErrors(res.data.fields);
				if (res.data.message) window.g.warning(res.data.message);
				this.emit('former:error', { res, data });
				return res;
			}

			this.markClean();
			this.emit('former:saved', { res, data });
			const keepDefaults = this.opts.onSuccess ? (await this.opts.onSuccess(res, this)) !== false : true;
			if (keepDefaults) {
				if (this.opts.toast !== false) window.g.success(this.opts.toast || t('Saved.'));
				if (this.opts.close !== false) this.close();
				await this.refresh(res);
			}
			return res;
		} finally {
			this.sending = false;
		}
	}

	// data.fields = { name: message } from a validation outcome
	// (data.validated === false) -> under the fields.
	applyFieldErrors(fields) {
		this.clearServerErrors();
		const marked = new Set();
		for (const name of Object.keys(fields || {})) {
			const el = this.form.querySelector('[name="' + CSS.escape(name) + '"]');
			if (!el) continue;
			window.g.fieldError(el, fields[name]);
			this.serverErrors.set(name, el);
			marked.add(el);
		}
		// the field scrolled to + focused is the first in DOM ORDER, never
		// the answer's key order — a jsonb data.fields arrives sorted by
		// key length, which on a long form jumps past the topmost error
		// (vux #111). form.elements IS the DOM order; focusing matches the
		// client-rule path (validator.focusField).
		let first = null;
		for (const el of this.form.elements) {
			if (marked.has(el)) { first = el; break; }
		}
		if (!first) first = marked.values().next().value || null; // value holders outside form.elements (edge case) keep the old pick
		if (first) {
			const root = first.closest('.selekt, .dsel, .fsel, .gedit') || first;
			const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
			root.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'center' });
			const target = root === first && first.type !== 'hidden' ? first : root.querySelector('input:not([type="hidden"]), select, textarea, button, [tabindex]');
			if (target && target.focus) target.focus({ preventScroll: true });
		}
	}

	clearServerErrors() {
		for (const el of this.serverErrors.values()) window.g.fieldError(el, null);
		this.serverErrors.clear();
	}

	// Returns the reload's promise (the pipeline awaits it - the submit
	// stays busy until the list shows the saved row).
	refresh(res) {
		const target = this.opts.refresh;
		if (!target) return;
		if (typeof target === 'function') return target(res);
		const el = typeof target === 'string' ? document.querySelector(target) : target;
		if (el && el.lister) return el.lister.reload();
		if (el && typeof el.reload === 'function') return el.reload(); // a lister instance was passed
	}

	emit(name, detail, cancelable) {
		const ev = new CustomEvent(name, { bubbles: true, cancelable: !!cancelable, detail: { former: this, ...detail } });
		this.form.dispatchEvent(ev);
		return ev;
	}
}
